import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getUserScreens } from "../../store/reducer/screen/screenSlice";
import { getDdOptionForUI } from "../../store/reducer/ddOption/ddOptionSlice";
import AccessDenied from "../AccessDenied";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const ProtectedRoute = ({ children, screenCode }) => {
  const dispatch = useDispatch();

  // 1. Selectors
  const { options } = useSelector((state) => state.ddOptions);
  const screenState = useSelector((state) => state.screens);

  const [isLoading, setIsLoading] = useState(true);
  const [isAllowed, setIsAllowed] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const checkAccess = async () => {
      try {
        const userSession = JSON.parse(localStorage.getItem("userSession"));

        // --- CHECK 1: Session ---
        if (!userSession?.id) {
          console.warn("Protected Route: No User Session ID found");
          if (isMounted) { setIsAllowed(false); setIsLoading(false); }
          return;
        }

        // --- CHECK 2: Prepare & Normalize Data ---
        let currentOptions = options || [];
        // Ensure userScreens is an array. Handle slice object structure.
        let currentUserScreens = Array.isArray(screenState.screens) ? screenState.screens : [];

        // --- CHECK 3: Ensure 'SCREEN' Definitions are Loaded ---
        // PROBLEM SOLVER: Even if options exist, we must check if "Screens" specifically are loaded.
        const hasScreenDefinitions = currentOptions.some(opt => opt.ui_name === 'Screens');

        if (!hasScreenDefinitions) {
          // console.log("Protected Route: fetching missing Screen Options...");
          const optRes = await dispatch(getDdOptionForUI()).unwrap();
          currentOptions = Array.isArray(optRes) ? optRes : (optRes?.data || []);
        }

        // --- CHECK 4: Ensure User Screens are Loaded ---
        if (currentUserScreens.length === 0) {
          // console.log("Protected Route: fetching user screens...");
          try {
            const screenRes = await dispatch(getUserScreens(userSession.id)).unwrap();
            // Backend returns [1, 2, 3], ensure we catch that
            currentUserScreens = Array.isArray(screenRes) ? screenRes : (screenRes?.screens || []);
          } catch (e) {
            console.error("Protected Route: Failed to fetch screens", e);
            currentUserScreens = [];
          }
        }

        // --- CHECK 5: Validation ---
        
        // Find the Dropdown Option for this ScreenCode (e.g. "OPP_LIST" -> ID 5)
        const targetOption = currentOptions.find(
          (opt) => opt.field_value === screenCode && opt.ui_name === "Screens"
        );

        if (!targetOption) {
          console.error(`Protected Route: ScreenCode "${screenCode}" not found in Options!`);
          // Debug help:
          // console.log("Available Options:", currentOptions.map(o => o.field_value));
          if (isMounted) { setIsAllowed(false); setIsLoading(false); }
          return;
        }

        // Check if User's ID list contains the Target ID
        // Robust check: Convert both to string to handle '1' vs 1
        const hasAccess = currentUserScreens.some((userScreenId) => {
            // If the ID is inside an object, extract it. If it's a raw number, use it.
            const idToCheck = (typeof userScreenId === 'object' && userScreenId !== null) 
                              ? userScreenId.id 
                              : userScreenId;
            return String(idToCheck) === String(targetOption.id);
        });

        if (isMounted) {
          if (!hasAccess) {
             console.warn(`Access Denied: User missing screen ID ${targetOption.id} for ${screenCode}`);
          }
          setIsAllowed(hasAccess);
          setIsLoading(false);
        }

      } catch (error) {
        console.error("Protected Route CRASHED:", error);
        if (isMounted) { setIsAllowed(false); setIsLoading(false); }
      }
    };

    checkAccess();

    return () => { isMounted = false; };
  }, [dispatch, screenCode]); // Dependencies

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-[#17171B]">
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} spin size="2xl" className="text-white mb-4" />
        </div>
      </div>
    );
  }

  return isAllowed ? children : <AccessDenied />;
};

export default ProtectedRoute;